import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical'

export type SerializedGroupMentionNode = Spread<
  {
    groupMentionName: string
    groupmentionValue: string
  },
  SerializedTextNode
>

function convertGroupMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent
  domNode.classList.add('group-mention')
  const valueContent = domNode.getAttribute('data-lexical-group-mention-value')
  if (valueContent !== null && textContent != null) {
    const node = $createGroupMentionNode(textContent, valueContent)
    return { node }
  }

  return null
}

export class GroupMentionNode extends TextNode {
  __groupMentionName: string
  __groupMentionValue: string

  static getType(): string {
    return 'groupmention'
  }

  static clone(node: GroupMentionNode): GroupMentionNode {
    return new GroupMentionNode(node.__groupMentionName, node.__groupMentionValue, node.__text, node.__key)
  }
  static importJSON(serializedNode: SerializedGroupMentionNode): GroupMentionNode {
    const node = $createGroupMentionNode(serializedNode.groupMentionName, serializedNode.groupmentionValue)
    node.setTextContent(serializedNode.text)
    node.setFormat(serializedNode.format)
    node.setDetail(serializedNode.detail)
    node.setMode(serializedNode.mode)
    node.setStyle(serializedNode.style)
    return node
  }

  constructor(groupMentionName: string, groupMentionValue: string, text?: string, key?: NodeKey) {
    super(text ?? groupMentionName, key)
    this.__groupMentionName = groupMentionName
    this.__groupMentionValue = groupMentionValue
  }

  exportJSON(): SerializedGroupMentionNode {
    return {
      ...super.exportJSON(),
      groupMentionName: this.__groupMentionName,
      groupmentionValue: this.__groupMentionValue,
      type: 'groupmention',
      version: 1,
    }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute('data-lexical-group-mention', 'true')
    dom.setAttribute('data-lexical-group-mention-value', this.__groupMentionValue)
    dom.className = 'group-mention'
    return dom
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span')
    element.classList.add('group-mention')
    element.setAttribute('data-lexical-group-mention', 'true')
    element.setAttribute('data-lexical-group-mention-value', this.__groupMentionValue)
    element.textContent = this.__text
    return { element }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-group-mention')) {
          return null
        }

        if (!domNode.hasAttribute('data-lexical-group-mention-value')) {
          return null
        }
        return {
          conversion: convertGroupMentionElement,
          priority: 1,
        }
      },
    }
  }

  isTextEntity(): true {
    return true
  }

  canInsertTextBefore(): boolean {
    return false
  }

  canInsertTextAfter(): boolean {
    return false
  }
}

export function $createGroupMentionNode(groupMentionName: string, groupMentionValue: string): GroupMentionNode {
  const mentionNode = new GroupMentionNode(groupMentionName, groupMentionValue)
  mentionNode.setMode('segmented').toggleDirectionless()
  return $applyNodeReplacement(mentionNode)
}

export function $isGroupMentionNode(node: LexicalNode | null | undefined): node is GroupMentionNode {
  return node instanceof GroupMentionNode
}
